import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useKeycloak } from "@react-keycloak/web";
import useBrainstormStore from "../../../hooks/useBrainstormStore";

import swal from "sweetalert2";
import { BsLightningCharge } from "react-icons/bs";
import { LuSparkles } from "react-icons/lu";
import { FaPlus, FaMinus } from "react-icons/fa";

import { generateBrainstormChallengeIdea } from "../../../services/Brainstorm/BrainstormService";

import Container from "../../Atoms/Container";
import Loader from "../../Atoms/Loader";
import ButtonMain from "../../Atoms/ButtonMain";
import ButtonSecondary from "../../Atoms/ButtonSecondary";
import TextArea from "../../Atoms/TextArea";
import Slider from "../../Atoms/Slider";

import ChallengeCard from "./Molecules/ChallengeCard";
import GuidingQuestionCard from "./Molecules/GuidingQuestionCard";
import EditChallengeModal from "./Molecules/EditChallengeModal";
import EditGuidingQuestionModal from "./Molecules/EditGuidingQuestionModal";

import './styles.css';

export default function BrainstormTemplate() {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const { keycloak } = useKeycloak();
    const KEYCLOACK_TOKEN = keycloak.token;

    const { 
        addChallenge, 
        deleteChallenge,
        updateChallenge,
        getBrainstormsByProject,
        getGuidingQuestionsByChallenge,
        addGuidingQuestion,
        addMultipleGuidingQuestions,
        updateGuidingQuestion,
        deleteGuidingQuestion,
        changeBrainstormStep,
    } = useBrainstormStore();
    
    const [challengeInputDisplay, setChallengeInputDisplay] = useState(false);
    const [questionInputDisplay, setQuestionInputDisplay] = useState(false);
    const [newChallenge, setNewChallenge] = useState('');
    const [currentBrainstorm, setCurrentBrainstorm] = useState(null);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [currentChallengeQuestions, setCurrentChallengeQuestions] = useState([]);
    const [newQuestion, setNewQuestion] = useState('');
    const [editModalShow, setEditModalShow] = useState(false);
    const [editingChallenge, setEditingChallenge] = useState(null);
    const [editQuestionModalShow, setEditQuestionModalShow] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [loadingAIquestion, setLoadingAIquestion] = useState(false);
    const [ideasPerQuestion, setIdeasPerQuestion] = useState(5);

    const MAX_IDEAS_PER_QUESTION = 5;
    
    const loadCurrentBrainstorm = useCallback(() => {
        const currentBrainstorm = getBrainstormsByProject(parseInt(projectId));
        setCurrentBrainstorm(currentBrainstorm[0]);
    }, [getBrainstormsByProject, projectId]);

    useEffect(() => {
        loadCurrentBrainstorm();
    }, [loadCurrentBrainstorm]);

    const handleSubmitDisabled = () => {
        if (!currentBrainstorm) return true;
      
        const challenges = currentBrainstorm.challenges;
        
        if (challenges.length === 0) return true;
        
        const hasActiveQuestions = challenges.some(challenge => {
            const questions = getGuidingQuestionsByChallenge(currentBrainstorm.id, challenge.id);
            return questions.some(question => question.selected === true);
        });
        
        return !hasActiveQuestions;
    }

    // CHALLENGE FUNCTIONS
    const handleAddChallenge = () => {
        const brainstormId = currentBrainstorm?.id;
        addChallenge(brainstormId,newChallenge);
        setNewChallenge('');
        loadCurrentBrainstorm();

        swal.fire({
            title: 'Sucesso!',
            text: 'Desafio adicionado com sucesso',
            timer: 1800,
            showConfirmButton: false,
            position: 'top-end',
            background: '#27272A',
            color: '#fff',
        });
    }

    const handleChallengeSelect = (challengeId) => {
        setSelectedChallenge(prev => {
            if (prev === challengeId) {
                return null;
            }

            const currentChallengeQuestions = getGuidingQuestionsByChallenge(currentBrainstorm?.id, challengeId);
            setCurrentChallengeQuestions(currentChallengeQuestions);

            return challengeId;
        });
    };

    const handleEditChallenge = (challengeId) => {
        const brainstormId = currentBrainstorm?.id;
        if (brainstormId) {
            const challenge = currentBrainstorm.challenges.find(c => c.id === challengeId);
            if (challenge) {
                setEditingChallenge(challenge);
                setEditModalShow(true);
            }
        }
    }

    const handleDeleteChallenge = (challengeId) => {
        const brainstormId = currentBrainstorm?.id;
        if (brainstormId) {
            swal.fire({
                title: 'Excluir desafio?',
                text: 'Esta ação não pode ser desfeita.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Sim, excluir!',
                cancelButtonText: 'Cancelar',
                background: '#27272A',
                color: '#fff',
            }).then((result) => {
                if (result.isConfirmed) {
                    deleteChallenge(brainstormId, challengeId);
                    loadCurrentBrainstorm();
                    
                    if (selectedChallenge === challengeId) {
                        setSelectedChallenge(null);
                        setCurrentChallengeQuestions([]);
                    }
                    
                    swal.fire({
                        title: 'Excluído!',
                        text: 'O desafio foi excluído com sucesso.',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                    });
                }
            });
        }
    }

    const handleConfirmChallengeEdit = (newText) => {
        const brainstormId = currentBrainstorm?.id;
        if (brainstormId && editingChallenge) {
            updateChallenge(brainstormId, editingChallenge.id, {text: newText});
            loadCurrentBrainstorm();
            
            swal.fire({
                title: 'Edição realizada com sucesso!',
                text: 'O desafio foi atualizado com sucesso.',
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    }

    const handleCloseEditChallengeModal = () => {
        setEditModalShow(false);
        setEditingChallenge(null);
    }

    // GUIDING QUESTION FUNCTIONS
    const handleAddQuestion = () => {
        const brainstormId = currentBrainstorm?.id;
        const currentChallengeQuestions = getGuidingQuestionsByChallenge(brainstormId, selectedChallenge);

        if (currentChallengeQuestions.length >= 5) {
            swal.fire({
                title: 'Limite atingido!',
                text: 'Apenas 5 perguntas norteadoras por desafio',
                timer: 2500,
                showConfirmButton: false,
                position: 'top-end',
                background: '#27272A',
                color: '#fff',
            });
            return;
        }

        addGuidingQuestion(brainstormId, newQuestion, selectedChallenge, true);
        setNewQuestion('');
        setCurrentChallengeQuestions(getGuidingQuestionsByChallenge(brainstormId, selectedChallenge));
        loadCurrentBrainstorm();
    }
    
    const handleGenerateWithAI = async () => {
        setLoadingAIquestion(true);

        const brainstormId = currentBrainstorm?.id;
        const currentSelectedChallenge = currentBrainstorm.challenges.find(challenge => challenge.id === selectedChallenge);
        const currentChallengeQuestions = getGuidingQuestionsByChallenge(brainstormId, selectedChallenge);
        if (currentChallengeQuestions.length >= 5) {
            swal.fire({
                title: 'Limite atingido!',
                text: 'Apenas 5 perguntas norteadoras por desafio',
                timer: 2500,
                showConfirmButton: false,
                position: 'top-end',
                background: '#27272A',
                color: '#fff',
            });
            setLoadingAIquestion(false);
            return;
        }

        const ideasToGenerate = MAX_IDEAS_PER_QUESTION - currentChallengeQuestions.length;
        const existentQuestions = currentChallengeQuestions.map(question => question.text);

        const serverResponse = await generateBrainstormChallengeIdea(parseInt(projectId), existentQuestions, currentSelectedChallenge.text, ideasToGenerate, KEYCLOACK_TOKEN);

        if (serverResponse.response.desafios_HMW.length > 0) {
            addMultipleGuidingQuestions(brainstormId, serverResponse.response.desafios_HMW.map(question => ({
                text: question,
                challengeId: currentSelectedChallenge.id,
                selected: true,
                in_knowledge_base: true,
            })));
            setCurrentChallengeQuestions(getGuidingQuestionsByChallenge(brainstormId, currentSelectedChallenge.id));
            loadCurrentBrainstorm();

            swal.fire({
                title: 'Pergunta norteadora gerada com sucesso!',
                text: 'A pergunta norteadora foi gerada com sucesso.',
                timer: 1500,
                position: 'top-end',
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
            });
        } else {
            swal.fire({
                title: 'Erro ao gerar pergunta norteadora com IA',
                text: serverResponse.data.message,
                background: '#27272A',
                timer: 1500,
                showConfirmButton: false,
            });

            return;
        }

        setLoadingAIquestion(false);
    }

    const handleSaveQuestion = () => {
        handleAddQuestion();
    }

    const handleQuestionToggle = (questionId, isActive) => {
        const brainstormId = currentBrainstorm?.id;
        if (brainstormId) {
            updateGuidingQuestion(brainstormId, questionId, { 
                selected: isActive 
            });
        }
        
        if (selectedChallenge) {
            const updatedQuestions = getGuidingQuestionsByChallenge(brainstormId, selectedChallenge);
            setCurrentChallengeQuestions(updatedQuestions);
        }
    }

    const handleEditGuidingQuestion = (questionId) => {
        const brainstormId = currentBrainstorm?.id;
        if (brainstormId) {
            const question = currentChallengeQuestions.find(q => q.id === questionId);
            if (question) {
                setEditingQuestion(question);
                setEditQuestionModalShow(true);
            }
        }
    }

    const handleDeleteGuidingQuestion = (questionId) => {
        const brainstormId = currentBrainstorm?.id;
        if (brainstormId) {
            swal.fire({
                title: 'Excluir pergunta norteadora?',
                text: 'Esta ação não pode ser desfeita.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Sim, excluir!',
                cancelButtonText: 'Cancelar',
                background: '#27272A',
                color: '#fff',
            }).then((result) => {
                if (result.isConfirmed) {
                    deleteGuidingQuestion(brainstormId, questionId);
                    loadCurrentBrainstorm();
                    
                    if (selectedChallenge) {
                        const updatedQuestions = getGuidingQuestionsByChallenge(brainstormId, selectedChallenge);
                        setCurrentChallengeQuestions(updatedQuestions);
                    }
                    
                    swal.fire({
                        title: 'Excluído!',
                        text: 'A pergunta norteadora foi excluída com sucesso.',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                    });
                }
            });
        }
    }

    const handleConfirmEditQuestion = (newText) => {
        const brainstormId = currentBrainstorm?.id;
        if (brainstormId && editingQuestion) {
            updateGuidingQuestion(brainstormId, editingQuestion.id, { text: newText });
            loadCurrentBrainstorm();
            
            // Refresh the current challenge questions
            if (selectedChallenge) {
                const updatedQuestions = getGuidingQuestionsByChallenge(brainstormId, selectedChallenge);
                setCurrentChallengeQuestions(updatedQuestions);
            }
            
            swal.fire({
                title: 'Pergunta atualizada!',
                text: 'A pergunta norteadora foi editada com sucesso.',
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    }

    const handleCloseEditQuestionModal = () => {
        setEditQuestionModalShow(false);
        setEditingQuestion(null);
    }

    const handleNextStep = () => {
        const brainstormId = currentBrainstorm?.id;
        changeBrainstormStep(brainstormId, 2);
        updateChallenge(brainstormId, selectedChallenge, {selected: true});

        navigate(`/brainstorm/ideas/${projectId}`, { state: { generateIdeas: true, ideasPerQuestion }});
    }
    
    return (
        <>
            <EditChallengeModal
                show={editModalShow}
                onHide={handleCloseEditChallengeModal}
                challenge={editingChallenge}
                onConfirm={handleConfirmChallengeEdit}
            />

            <EditGuidingQuestionModal
                show={editQuestionModalShow}
                onHide={handleCloseEditQuestionModal}
                question={editingQuestion}
                onConfirm={handleConfirmEditQuestion}
            />
            
            <Container>
                <section id="brainstorm-header" className="mb-3">
                    <div className="d-flex flex-row align-items-center justify-content-between">
                        <div className='d-flex flex-row align-items-center gap-2'>
                            <div className='d-flex flex-row align-items-center gap-2'>
                                <div style={{ backgroundColor: '#FFD6A8', padding: '3px 8px', borderRadius: '15px' }}>
                                    <BsLightningCharge color='#CA3500' />
                                </div>
                            </div>
                            
                            <h3 className="mt-2">Brainstorm</h3>
                        </div>

                        <div className="d-flex align-items-center gap-3">
                            <Slider
                                min={1}
                                max={20}
                                value={ideasPerQuestion}
                                onChange={setIdeasPerQuestion}
                                disabled={handleSubmitDisabled()}
                                label="ideias por pergunta"
                            />
                            <ButtonMain
                                disabled={handleSubmitDisabled()}
                                onClick={handleNextStep}
                            >
                                <LuSparkles /> Gerar ideias
                            </ButtonMain>
                        </div>
                    </div>

                    <p className="text-secondary">Explore desafios e oportunidades para gerar conceitos inovadores</p>
                </section>
            </Container>

            <div className="brainstorm-header" />

            <section id="brainstorm-content">
                <Container>    
                    <div className="row" style={{ height: '500px' }}>
                        <div className="col-12 col-md-6 brainstorm-border-right">
                            <div className="d-flex flex-row align-items-center justify-content-between mt-2">
                                <p>Desafio ou oportunidade</p>
                                <button
                                    className="btn brainstorm-challenge-button-plus"
                                    onClick={() => setChallengeInputDisplay(!challengeInputDisplay)}>
                                        {challengeInputDisplay ? <FaMinus size={12} color="fff" /> : <FaPlus size={12} color="fff" />}
                                </button>
                            </div>
                            
                            <div className="brainstorm-scrollable-content mt-2"> 
                                {(challengeInputDisplay || currentBrainstorm?.challenges.length === 0) && (
                                    <>
                                        <TextArea
                                            placeholder="Descreva o desafio ou oportunidade"
                                            maxLength={5000}
                                            onChange={(e) => setNewChallenge(e.target.value)}
                                            value={newChallenge}
                                            containerStyle={{ padding: '0px' }}
                                        />

                                        <div className="d-flex flex-column gap-2 align-items-end mb-3">
                                            <p className="text-secondary">5000 caracteres</p>

                                            <button
                                                disabled={newChallenge === ''}
                                                className="btn brainstorm-challenge-button-save w-25 mt-2"
                                                onClick={() => handleAddChallenge()}
                                            >
                                                Salvar
                                            </button>
                                        </div>
                                    </>            
                                )}

                                {currentBrainstorm?.challenges.length > 0 && (
                                    <div className="mt-2 me-2">
                                        {currentBrainstorm?.challenges.map((challenge) => (
                                            <ChallengeCard
                                                key={challenge.id}
                                                id={challenge.id}
                                                description={challenge.text}
                                                origin="brainstorm"
                                                icon="lightning"
                                                isSelected={selectedChallenge === challenge.id}
                                                onSelect={handleChallengeSelect}
                                                onEdit={handleEditChallenge}
                                                onDelete={handleDeleteChallenge}
                                            />
                                        ))}
                                        
                                        <div
                                            onClick={() => setChallengeInputDisplay(!challengeInputDisplay)}
                                            className="text-center new-challenge-button-list"
                                        >
                                            <p>+ Add novo desafio</p>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <div className="d-flex flex-row align-items-center justify-content-between mt-2">
                                <p>Perguntas norteadoras</p>

                                <div>
                                    <button
                                        className="btn"
                                        onClick={() => setQuestionInputDisplay(!questionInputDisplay)}
                                    >
                                        {questionInputDisplay ? <FaMinus size={12} /> : <FaPlus size={12} />}
                                    </button>
                                </div>
                            </div>

                            <div className="brainstorm-scrollable-content-guiding-questions">
                            {(questionInputDisplay || currentChallengeQuestions.length === 0) && selectedChallenge && (
                                    <div className="mt-2 me-2">
                                        <TextArea
                                            placeholder="Descreva a pergunta norteadora. Como podemos ...?"
                                            maxLength={500}
                                            onChange={(e) => setNewQuestion(e.target.value)}
                                            value={newQuestion}
                                            containerStyle={{ padding: '0px' }}
                                            textAreaStyle={{ minHeight: '100px' }}
                                        />
                                        <div className="guiding-question-actions-header">
                                            <p className="text-secondary mt-2">500 caracteres</p>

                                            <div className="guiding-question-buttons">
                                                <ButtonSecondary
                                                    disabled={loadingAIquestion}
                                                    icon={<LuSparkles size={14} />}
                                                    onClick={() => handleGenerateWithAI()}
                                                >
                                                    {loadingAIquestion ? <Loader animation="border" size={14} /> : 'Gerar com IA'}
                                                </ButtonSecondary>

                                                <ButtonMain
                                                    disabled={newQuestion === ''}
                                                    onClick={() => handleSaveQuestion()}
                                                    className="guiding-question-save-button"
                                                >
                                                    Salvar
                                                </ButtonMain>
                                            </div>
                                        </div>
                                    </div>
                            )}

                            {currentChallengeQuestions.length > 0 && (
                                    <div className="mt-3 me-2">
                                        {currentChallengeQuestions.map((question) => (
                                            <GuidingQuestionCard 
                                                key={question.id} 
                                                question={question}
                                                isActive={question.selected || false}
                                                onToggle={handleQuestionToggle}
                                                onEdit={handleEditGuidingQuestion}
                                                onDelete={handleDeleteGuidingQuestion}
                                            />
                                        ))}
                                    </div>
                            )}
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </>
    )
}